import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, CartesianGrid,
} from "recharts";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmt, symbol, monthLabel, currentMonth } from "@/lib/format";
import { Card, PageHeader } from "@/components/Kit";
import { TrendUp, TrendDown, Scales, ArrowUpRight, ArrowDownRight } from "@phosphor-icons/react";

const ChartTooltip = ({ active, payload, label, cur }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0A0A0A]/90 backdrop-blur-xl border border-white/10 rounded-md px-3 py-2 text-xs">
      <p className="text-[#A1A1AA] mb-1">{monthLabel(label) || label}</p>
      {payload.map((p) => (
        <p key={p.name} className="num" style={{ color: p.color || p.fill }}>
          {p.name}: {fmt(p.value, cur)}
        </p>
      ))}
    </div>
  );
};

function StatCard({ label, value, cur, icon: Icon, color, testid }) {
  return (
    <Card className="p-6 hover:bg-[#1A1A1A] transition-colors">
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs uppercase tracking-wider text-[#A1A1AA]">{label}</span>
        <Icon size={20} weight="duotone" style={{ color }} />
      </div>
      <p className="num text-3xl font-medium tracking-tight" style={{ color }} data-testid={testid}>
        {fmt(value, cur)}
      </p>
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const cur = user?.baseCurrency || "USD";
  const [summary, setSummary] = useState({ income: 0, expense: 0, net: 0 });
  const [trends, setTrends] = useState([]);
  const [byCat, setByCat] = useState([]);
  const [txns, setTxns] = useState([]);
  const [cats, setCats] = useState({});
  const month = currentMonth();

  useEffect(() => {
    const load = async () => {
      const [s, t, b, tx, c] = await Promise.all([
        api.get(`/analytics/summary?month=${month}`),
        api.get(`/analytics/trends?months=6`),
        api.get(`/analytics/by-category?type=EXPENSE&month=${month}`),
        api.get(`/transactions`),
        api.get(`/categories`),
      ]);
      setSummary(s.data);
      setTrends(t.data);
      setByCat(b.data);
      setTxns(tx.data.slice(0, 6));
      const map = {};
      c.data.forEach((cat) => (map[cat.id] = cat));
      setCats(map);
    };
    load();
  }, [month]);

  return (
    <div className="p-8 md:p-12 animate-fade-up">
      <PageHeader title="Dashboard" subtitle={`Overview for ${monthLabel(month)}`} />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <StatCard label="Income" value={summary.income} cur={cur} icon={TrendUp} color="#10B981" testid="stat-income" />
        <StatCard label="Expenses" value={summary.expense} cur={cur} icon={TrendDown} color="#F43F5E" testid="stat-expense" />
        <StatCard label="Net Balance" value={summary.net} cur={cur} icon={Scales} color={summary.net >= 0 ? "#00E5FF" : "#F43F5E"} testid="stat-net" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <Card className="lg:col-span-2 p-6">
          <h3 className="font-heading font-bold text-lg mb-6">Income vs Expenses</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trends} margin={{ left: -10, right: 8 }}>
                <defs>
                  <linearGradient id="gInc" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10B981" stopOpacity={0.25} />
                    <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gExp" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#F43F5E" stopOpacity={0.25} />
                    <stop offset="100%" stopColor="#F43F5E" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="month" tickFormatter={monthLabel} stroke="#52525B" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#52525B" fontSize={11} tickLine={false} axisLine={false} width={48} />
                <Tooltip content={<ChartTooltip cur={cur} />} />
                <Area type="monotone" dataKey="income" name="Income" stroke="#10B981" strokeWidth={2} fill="url(#gInc)" />
                <Area type="monotone" dataKey="expense" name="Expense" stroke="#F43F5E" strokeWidth={2} fill="url(#gExp)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="font-heading font-bold text-lg mb-6">Spending by Category</h3>
          {byCat.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-sm text-[#52525B]">No expenses yet</div>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={byCat} dataKey="amount" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2} stroke="#111111" strokeWidth={2}>
                    {byCat.map((e) => <Cell key={e.categoryId} fill={e.color} />)}
                  </Pie>
                  <Tooltip content={<ChartTooltip cur={cur} />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>
      </div>

      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-bold text-lg">Recent Transactions</h3>
          <Link to="/transactions" className="text-sm text-cyan hover:underline" data-testid="view-all-txns">View all</Link>
        </div>
        {txns.length === 0 ? (
          <p className="text-sm text-[#52525B] py-8 text-center">No transactions yet. Add your first one.</p>
        ) : (
          <div className="divide-y divide-white/[0.06]">
            {txns.map((t) => {
              const isIncome = t.type === "INCOME";
              const cat = cats[t.categoryId];
              return (
                <div key={t.id} className="flex items-center justify-between py-3" data-testid="dashboard-txn-row">
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-md flex items-center justify-center" style={{ backgroundColor: (cat?.color || "#A1A1AA") + "22" }}>
                      {isIncome ? <ArrowUpRight size={18} className="text-income" /> : <ArrowDownRight size={18} className="text-expense" />}
                    </div>
                    <div>
                      <p className="text-sm font-medium">{t.description || cat?.name || "Transaction"}</p>
                      <p className="text-xs text-[#52525B]">{cat?.name || "Uncategorized"} · {t.date}</p>
                    </div>
                  </div>
                  <span className={`num text-sm font-medium ${isIncome ? "text-income" : "text-expense"}`}>
                    {isIncome ? "+" : "-"}{fmt(t.amount, t.currency)}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}
